import { useState } from 'react'
import GooglePlacesAutocomplete from 'react-google-places-autocomplete'
import { useQuery } from 'react-query'
import { MenuPlacement } from 'react-select'
import placeService from '../services/placeService'
import { GoogleSuggestion } from '../types/google-places'
import { Place } from '../types/utils'
import { PlaceClaimStatus } from '../utils/config'
import { reactSelectStyles, reactSelectTheme } from '../utils/reactSelect'

interface GoogleSearchProps {
  label?: string
  handleSelect: (selected: Pick<Place, 'googlePlaceId' | 'description'>) => void
  menuPlacement?: MenuPlacement
  single?: boolean
}

const GoogleSearch = ({ single, label, handleSelect, menuPlacement = 'auto' }: GoogleSearchProps) => {
  const [selected, setSelected] = useState<GoogleSuggestion | undefined>()

  const {
    data: places,
    isLoading,
    isError,
  } = useQuery(['places', { claimStatus_in: [PlaceClaimStatus.CLAIMED, PlaceClaimStatus.PENDING] }], () =>
    placeService.getAll({ where: { claimStatus_in: [PlaceClaimStatus.CLAIMED, PlaceClaimStatus.PENDING] } })
  )

  if (isLoading) {
    return <div className="h-9 animate-pulse rounded-md bg-gray-300" />
  }

  if (isError) {
    return (
      <p>Cannot load the places service, please refresh the page, if the issue persist contact the support team.</p>
    )
  }

  const handleClick = () => {
    if (selected) {
      handleSelect({
        googlePlaceId: selected.value.place_id,
        description: selected.value.description,
      })
    }
  }

  const handleSingle = ({ label, value }: GoogleSuggestion) => {
    if (label && value) {
      handleSelect({
        googlePlaceId: value.place_id,
        description: value.description,
      })
    }
  }

  return (
    <div>
      {label && <p className="mb-1 block text-sm font-medium text-gray-700">{label}</p>}

      <div className="flex w-full">
        <div className="flex-1">
          <GooglePlacesAutocomplete
            autocompletionRequest={{
              types: ['establishment'],
            }}
            apiKey={process.env.REACT_APP_GOOGLE_PLACES}
            selectProps={{
              value: selected,
              onChange: single ? handleSingle : setSelected,
              styles: reactSelectStyles,
              theme: reactSelectTheme,
              menuPlacement: menuPlacement,
              menuPortalTarget: document.body,
              isClearable: !single,
              filterOption: ({ data, value }) => {
                if (places && places.length > 0) {
                  const filter = places.some(({ googlePlaceId }: Place) => googlePlaceId === value)

                  if (filter) return null
                }

                return data
              },
            }}
          />
        </div>

        {!single && (
          <button type="button" className="btn btn-primary ml-3" onClick={handleClick} disabled={!selected}>
            Add
          </button>
        )}
      </div>
    </div>
  )
}

export default GoogleSearch
